import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useLocation, useNavigate, useParams } from 'react-router';
import { BadgeCheck, ExternalLink } from 'lucide-react';

import { BackLink } from '@/components/admin/detail-parts';
import { StatusBadge } from '@/components/ui/status-badge';
import { adminInitials, type AdminTalentDetail } from '@/contracts';
import { useLocalizedName } from '@/i18n/localized-name';
import { useCityLabel } from '@/i18n/use-city-label';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate, listReturnPath } from '@/lib/detail';
import { useAccountStatusLabel } from '@/lib/labels';
import { publicProfileUrl } from '@/lib/public-site';
import { DeleteTalentPanel, RemovePhotoPanel } from './talent-action-panels';
import { DetailGate } from '@/components/admin/detail-gate';
import { useSpecialtiesQuery, useTalentQuery } from '@/lib/queries';
import { accountTone } from '@/lib/status';
import { Card } from '@/components/ui/card';
import { ContactSection, PersonalSection, ProfileSection } from './talent-profile-sections';
import { CredentialsSection } from './talent-credentials-section';
import { VideoIntroSection } from './talent-video-section';
import { PlanSection, ActivitySection } from './talent-plan-activity-sections';

const LIST_PATH = '/talents';

/**
 * One talent, everything the console knows: who they are and how to reach them, what
 * they have claimed and where each claim stands in the document audit, what they pay
 * for, and how much attention the profile gets — with the three things an admin can do
 * about them.
 *
 * Every section whose data is new on the API renders against today's response too: a
 * field that has not arrived shows a dash, a list that has not arrived says it is empty.
 */
export function TalentDetailRoute() {
  const candidateId = useParams().candidateId ?? '';
  const t = useTranslations('admin.talentDetail');
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = listReturnPath(location.state, LIST_PATH);

  const talent = useTalentQuery(candidateId);

  return (
    <DetailGate
      query={talent}
      notFound={{
        title: t('notFound.title'),
        hint: t('notFound.hint'),
        backTo,
        backLabel: t('back'),
      }}
    >
      {(data) => (
        <TalentDetail
          talent={data}
          backTo={backTo}
          onDeleted={() => void navigate(backTo, { replace: true })}
        />
      )}
    </DetailGate>
  );
}

function TalentDetail({
  talent,
  backTo,
  onDeleted,
}: {
  talent: AdminTalentDetail;
  backTo: string;
  onDeleted: () => void;
}) {
  const t = useTranslations('admin.talentDetail');
  const admin = useTranslations('admin');
  const common = useTranslations('common');
  const cityLabel = useCityLabel();
  const accountStatusLabel = useAccountStatusLabel();
  const name = useLocalizedName();
  const [panel, setPanel] = useState<'none' | 'photo' | 'delete'>('none');

  // The detail carries the Arabic name; the catalogue gives the admin's language.
  const specialties = useSpecialtiesQuery();
  const specialty = useMemo(() => {
    const match = specialties.data?.find((s) => s.id === talent.specialtyId);
    return match ? name(match) : (talent.specialtyNameAr ?? talent.specialtyId);
  }, [specialties.data, talent.specialtyId, talent.specialtyNameAr, name]);

  return (
    <div className="space-y-6">
      <BackLink to={backTo} label={t('back')} />

      <Card asChild>
        <header className="p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
              {talent.photoUrl ? (
                <img
                  src={talent.photoUrl}
                  alt={admin('talents.photo.alt', { name: talent.fullName })}
                  className="h-16 w-16 shrink-0 rounded-full border border-conc-100 object-cover"
                />
              ) : (
                <span
                  aria-hidden
                  data-slot="monogram"
                  className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-navy text-[16px] font-bold text-white"
                >
                  {adminInitials(talent.fullName)}
                </span>
              )}
              <div className="min-w-0">
                <h2 className="flex items-center gap-1.5 text-lg font-bold text-navy" dir="auto">
                  {talent.fullName}
                  {talent.verified && (
                    <BadgeCheck aria-hidden className="h-5 w-5 shrink-0 text-pitch" />
                  )}
                </h2>
                <p className="mt-0.5 text-[12.5px] text-conc-500">
                  {specialty}
                  {cityLabel(talent.city) && ` · ${cityLabel(talent.city)}`}
                  {' · '}
                  <span className="num">
                    {admin('table.yearsValue', { count: talent.yearsOfExperience })}
                  </span>
                </p>
                <p className="mt-0.5 text-[12px] text-conc-400">
                  <span className="num">
                    {t('joined', { date: isoDate(talent.createdAt) ?? '' })}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge tone={talent.verified ? 'approved' : 'pending'}>
                {talent.verified ? admin('status.verified') : admin('status.pending')}
              </StatusBadge>
              {talent.accountStatus && (
                <StatusBadge tone={accountTone(talent.accountStatus)}>
                  {accountStatusLabel(talent.accountStatus)}
                </StatusBadge>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-conc-100 pt-4">
            <a
              href={publicProfileUrl(talent.slug)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-conc-100 bg-white px-3.5 py-2 text-[12.5px] font-bold text-pitch-700 transition hover:border-pitch"
            >
              <ExternalLink aria-hidden className="h-4 w-4" />
              {common('openProfile')}
            </a>
            {talent.photoUrl && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPanel((current) => (current === 'photo' ? 'none' : 'photo'))}
              >
                {admin('talents.photo.remove')}
              </Button>
            )}
            <Button
              type="button"
              variant="danger-soft"
              size="sm"
              onClick={() => setPanel((current) => (current === 'delete' ? 'none' : 'delete'))}
            >
              {common('delete')}
            </Button>
          </div>
        </header>
      </Card>

      {panel === 'photo' && (
        <RemovePhotoPanel
          talent={talent}
          onCancel={() => setPanel('none')}
          onRemoved={() => setPanel('none')}
        />
      )}
      {panel === 'delete' && (
        <DeleteTalentPanel
          talent={talent}
          onCancel={() => setPanel('none')}
          onDeleted={onDeleted}
        />
      )}

      <div className="grid gap-6 xl:grid-cols-2">
        <ContactSection talent={talent} />
        <PersonalSection talent={talent} />
      </div>

      <ProfileSection talent={talent} specialty={specialty} />
      <CredentialsSection credentials={talent.credentials} />
      <VideoIntroSection video={talent.videoIntro} talentName={talent.fullName} />

      <div className="grid gap-6 xl:grid-cols-2">
        <PlanSection talent={talent} />
        <ActivitySection talent={talent} />
      </div>
    </div>
  );
}
